/**
 * Settings → Organization → Branding (tier offer: Pro = your logo & colors,
 * Enterprise = white label). Saves into the tenant's `styling`; the color
 * re-skins the app instantly via the branding event.
 */
import React, { useEffect, useRef, useState } from 'react';
import { PaintBrushIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useUpdateBranding } from '../../services/settings';
import { uploadFile, useImageUrl } from '../../services/uploads';
import { announceBrandingUpdate, hexToTriplet } from '../../lib/branding';
import type { TenantStyling } from '../../types/identity';
import FeatureUpgradeNotice from '../common/FeatureUpgradeNotice';
import { FEATURE, planHasFeature } from './tiers/tierCatalog';

const DEFAULT_PURPLE = '#845AD4';
const DEFAULT_ORANGE = '#EE8E4D';

export default function BrandingSettings() {
  const { activeMembership, updateTenantStyling } = useAuth();
  const tenant = activeMembership?.tenant;
  const plan = tenant?.subscription_plan ?? 'free';
  const hasBranding = planHasFeature(FEATURE.branding, plan);
  const isEnterprise = planHasFeature(FEATURE.whiteLabel, plan);
  const styling: TenantStyling = tenant?.styling ?? {};

  const [color, setColor] = useState(styling.primary_color ?? DEFAULT_PURPLE);
  // The accent family (our orange) is part of "your logo & colors" too
  // (Aleix, 2026-09-10).
  const [accent, setAccent] = useState(styling.secondary_color ?? DEFAULT_ORANGE);
  const [logoKey, setLogoKey] = useState<string | null>(styling.logo_key ?? null);
  const [whiteLabel, setWhiteLabel] = useState(Boolean(styling.white_label));

  // The session hydrates after this form mounts, so seeding the pickers from
  // `styling` only at mount left them showing Tereiko's defaults on a branded
  // workspace (Aleix, 2026-09-10: "I still see the Tereiko colors instead of
  // the ones I selected"). Re-sync whenever the saved styling changes — but
  // ONLY while the form is untouched.
  //
  // Without that guard the sync also ran over the user's own edits: any
  // background refetch of the session reset the pickers to the saved values —
  // to the Tereiko defaults when nothing was saved — and the next Save then
  // sent nulls and wiped the workspace's colours AND its logo (Aleix,
  // 2026-09-11: colours and logo gone from a workspace he had just branded).
  const [pristine, setPristine] = useState(true);
  useEffect(() => {
    if (!pristine) return;
    setColor(styling.primary_color ?? DEFAULT_PURPLE);
    setAccent(styling.secondary_color ?? DEFAULT_ORANGE);
    setLogoKey(styling.logo_key ?? null);
    setWhiteLabel(Boolean(styling.white_label));
  }, [
    pristine,
    styling.primary_color,
    styling.secondary_color,
    styling.logo_key,
    styling.white_label,
  ]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [logoUrl] = useImageUrl(logoKey);
  const updateBranding = useUpdateBranding();

  if (!hasBranding) {
    return (
      <section>
        <h2 className="text-lg font-semibold text-text-primary mb-1">Branding</h2>
        <p className="text-sm text-text-secondary mb-6">
          Make the workspace look like your organization
        </p>
        <FeatureUpgradeNotice
          feature="Branding"
          description="Put your own logo in the sidebar and re-skin the platform in your brand color. White label is available on Enterprise."
          currentPlan={plan}
          icon={<PaintBrushIcon className="w-8 h-8" />}
        />
      </section>
    );
  }

  const handleLogoPick = async (file: File) => {
    if (!tenant) return;
    if (!['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'].includes(file.type)) {
      toast.error('Logo must be a PNG, JPG, WebP, or SVG image.');
      return;
    }
    setUploading(true);
    try {
      const result = await uploadFile({
        file,
        resource_type: 'tenant_logo',
        resource_id: tenant.id,
      });
      setPristine(false);
      setLogoKey(result.key);
    } catch {
      toast.error('Logo upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (
      (color !== DEFAULT_PURPLE && !hexToTriplet(color)) ||
      (accent !== DEFAULT_ORANGE && !hexToTriplet(accent))
    ) {
      toast.error('Pick a valid color.');
      return;
    }
    const payload = {
      primary_color: color === DEFAULT_PURPLE ? null : color,
      secondary_color: accent === DEFAULT_ORANGE ? null : accent,
      // Only send the logo when this form actually holds one, or when the user
      // cleared one it knew about. Sending null from a form that simply had
      // not hydrated yet is how an uploaded logo disappeared.
      ...(logoKey !== null || styling.logo_key ? { logo_key: logoKey } : {}),
      ...(isEnterprise ? { white_label: whiteLabel } : {}),
    };
    try {
      const { styling: saved } = await updateBranding.mutateAsync(payload);
      // Push it into the live session too, or the sidebar, the footer and
      // this very form keep showing the old logo until the next hydrate.
      updateTenantStyling(saved as Record<string, unknown>);
      announceBrandingUpdate(saved as TenantStyling);
      setPristine(true);
      toast.success('Branding saved');
    } catch {
      toast.error('Could not save branding. Please try again.');
    }
  };

  const handleReset = async () => {
    setColor(DEFAULT_PURPLE);
    setAccent(DEFAULT_ORANGE);
    setLogoKey(null);
    setWhiteLabel(false);
    try {
      const { styling: saved } = await updateBranding.mutateAsync({
        primary_color: null,
        secondary_color: null,
        logo_key: null,
        ...(isEnterprise ? { white_label: false } : {}),
      });
      // Push it into the live session too, or the sidebar, the footer and
      // this very form keep showing the old logo until the next hydrate.
      updateTenantStyling(saved as Record<string, unknown>);
      announceBrandingUpdate(saved as TenantStyling);
      setPristine(true);
      toast.success('Back to Tereiko defaults');
    } catch {
      toast.error('Could not reset branding.');
    }
  };

  return (
    <section>
      <h2 className="text-lg font-semibold text-text-primary mb-1">Branding</h2>
      <p className="text-sm text-text-secondary mb-6">
        Your logo and brand color, across the whole workspace
      </p>

      <div className="card p-6 space-y-6 max-w-2xl">
        {/* Logo */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl border border-border-primary bg-surface-elevated flex items-center justify-center overflow-hidden shrink-0">
            {logoUrl ? (
              <img src={logoUrl} alt="Workspace logo" className="w-full h-full object-contain" />
            ) : (
              <PaintBrushIcon className="w-6 h-6 text-text-tertiary" />
            )}
          </div>
          <div className="flex-1">
            <p className="body-small font-semibold text-text-primary">Workspace logo</p>
            <p className="text-xs text-text-secondary">
              Replaces the Tereiko mark in the sidebar. Square images look best.
            </p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleLogoPick(f);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="btn btn-outline btn-sm rounded-full disabled:opacity-50"
          >
            {uploading ? 'Uploading…' : logoKey ? 'Replace' : 'Upload'}
          </button>
          {logoKey && (
            <button
              type="button"
              onClick={() => { setPristine(false); setLogoKey(null); }}
              className="btn btn-ghost btn-sm rounded-full"
            >
              Remove
            </button>
          )}
        </div>

        {/* Color */}
        <div className="flex items-center gap-4">
          <input
            type="color"
            value={color}
            onChange={(e) => { setPristine(false); setColor(e.target.value); }}
            aria-label="Brand color"
            className="w-16 h-10 rounded-lg border border-border-primary bg-transparent cursor-pointer"
          />
          <div className="flex-1">
            <p className="body-small font-semibold text-text-primary">Brand color</p>
            <p className="text-xs text-text-secondary">
              Replaces the Tereiko purple on buttons, links, and highlights.
            </p>
          </div>
          <span className="text-sm tabular-nums text-text-secondary">{color.toUpperCase()}</span>
        </div>

        {/* Accent color */}
        <div className="flex items-center gap-4">
          <input
            type="color"
            value={accent}
            onChange={(e) => { setPristine(false); setAccent(e.target.value); }}
            aria-label="Accent color"
            className="w-16 h-10 rounded-lg border border-border-primary bg-transparent cursor-pointer"
          />
          <div className="flex-1">
            <p className="body-small font-semibold text-text-primary">Accent color</p>
            <p className="text-xs text-text-secondary">
              Replaces the Tereiko orange on secondary buttons, chips, and highlights.
            </p>
          </div>
          <span className="text-sm tabular-nums text-text-secondary">{accent.toUpperCase()}</span>
        </div>

        {/* White label — Enterprise */}
        {isEnterprise && (
          <label className="flex items-center gap-4 cursor-pointer">
            <input
              type="checkbox"
              checked={whiteLabel}
              onChange={(e) => { setPristine(false); setWhiteLabel(e.target.checked); }}
              className="w-5 h-5 rounded accent-primary-500 focus-visible:ring-2"
            />
            <div className="flex-1">
              <p className="body-small font-semibold text-text-primary">White label</p>
              <p className="text-xs text-text-secondary">
                Also replaces the Tereiko branding in the footer with your organization's name.
              </p>
            </div>
          </label>
        )}

        <div className="flex items-center gap-3 pt-2 border-t border-border-primary/40">
          <button
            type="button"
            onClick={handleSave}
            disabled={updateBranding.isPending || uploading}
            className="btn btn-primary rounded-full active:scale-[0.97] disabled:opacity-50"
          >
            {updateBranding.isPending ? 'Saving…' : 'Save branding'}
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={updateBranding.isPending}
            className="btn btn-ghost rounded-full"
          >
            Reset to Tereiko defaults
          </button>
        </div>
      </div>
    </section>
  );
}
